import { Box, Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Portal } from '@chakra-ui/react';
import { createColumnHelper, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { BiMenu } from 'react-icons/bi';
import { RiBook2Line, RiStackLine, RiThumbUpLine } from 'react-icons/ri';

import {
    getTradeSide,
    getTradeValueAsPercentOfNav,
} from '@/cip/Optimizer/OptimizationResults/TradeExecution/tradeExecutionUtils';
import { CopiableGuid } from '@/components/CopiableGuid';

import { type EnrichedOptimizerTrade } from '../../../_openapi/portfolio-optimizer';
import { AssetClassTag } from '../../../widgets/AssetClassTag';
import { baseCol, moneyCol, numberCol, percentageCol, textCol } from '../../../widgets/Table/columnHelpers/colDef';
import { makeFieldAccessors } from '../../../widgets/Table/columnHelpers/makeFieldAccessors';
import { Table } from '../../../widgets/Table/Table';
import { TextCell } from '../../../widgets/Table/TextCell';
import { TradeSideTag } from '../../../widgets/TradeSideTag';

const columnHelper = createColumnHelper<EnrichedOptimizerTrade>();
const asset = makeFieldAccessors(columnHelper, (row) => row.asset);
export const ExcludedTrades = ({
    trades,
    removeExclusion,
    viewAsset,
    viewPortfolio,
}: {
    trades: EnrichedOptimizerTrade[];
    removeExclusion: (trade: EnrichedOptimizerTrade) => void;
    viewAsset: (sId: string) => void;
    viewPortfolio: (pId: string) => void;
}) => {
    const table = useReactTable({
        defaultColumn: {
            enableColumnFilter: false,
        },
        data: trades,
        initialState: {
            expanded: true,
        },
        columns: [
            columnHelper.display({
                id: 'contextMenu',
                cell: ({ row }) => (
                    <Menu isLazy>
                        <MenuButton
                            size="xs"
                            as={IconButton}
                            aria-label="Options"
                            icon={<Icon boxSize={3} as={BiMenu} />}
                            w="20px"
                            minW="20px"
                            h="20px"
                            variant="outline"
                            borderRadius="sm"
                        />
                        <Portal>
                            <MenuList>
                                <MenuItem
                                    icon={<Icon boxSize={4} as={RiBook2Line} />}
                                    onClick={() => viewPortfolio(row.original.pId)}
                                >
                                    Portfolio details
                                </MenuItem>
                                <MenuItem
                                    icon={<Icon boxSize={4} as={RiStackLine} />}
                                    onClick={() => viewAsset(row.original.sId)}
                                >
                                    Security details
                                </MenuItem>
                                <MenuItem
                                    icon={<Icon boxSize={4} as={RiThumbUpLine} />}
                                    onClick={() => removeExclusion(row.original)}
                                >
                                    Include Trade
                                </MenuItem>
                            </MenuList>
                        </Portal>
                    </Menu>
                ),
                enableSorting: false,
                meta: {
                    isDisplayCol: true,
                },
            }),
            columnHelper.accessor('portfolioGuid', {
                ...baseCol({ id: 'portfolioGuid' }),
                cell: ({ getValue }) => <CopiableGuid className="text-white" guid={getValue()} showBorder={false} />,
            }),
            columnHelper.accessor('aC', textCol({ id: 'aC' })),
            columnHelper.accessor('allocationType', textCol({ id: 'allocationType' })),
            columnHelper.accessor(getTradeSide, {
                ...baseCol({ id: 'side' }),
                cell: ({ getValue }) => <TradeSideTag side={getValue()} />,
            }),
            columnHelper.accessor((row) => row.asset?.assetClass, {
                ...baseCol({ id: 'assetClass' }),
                cell: ({ getValue }) => <AssetClassTag assetClass={getValue()} />,
            }),
            asset.text('assetType'),
            asset.text('symbol'),
            asset.text('description'),
            columnHelper.accessor('tradeQuantity', numberCol({ id: 'tradeQuantity' })),
            columnHelper.accessor('orderPrice', moneyCol({ id: 'orderPrice' })),
            columnHelper.accessor('tradeValue', moneyCol({ id: 'tradeValue' })),
            columnHelper.accessor(getTradeValueAsPercentOfNav, percentageCol({ id: 'percentOfNav' })),
        ],
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        sortDescFirst: false,
    });

    return <Table table={table} allowXOverflow />;
};
